/**
 * Getting the store apps to people who land on the website (P0211).
 *
 * Three pieces, each the usual one for its place:
 *   - iPhone and iPad: Apple's own Smart App Banner, from one line in public/index.html (Safari shows it; nothing here).
 *   - Android: a slim bar at the top of the page in the phone's browser (GetTheAppBar), since Chrome has no dependable
 *     banner of its own for a Play Store app. Closed once, it stays closed on that phone.
 *   - The landing page: the official store badges, the one for the visitor's phone, both on a computer.
 * None of it shows inside the store apps themselves.
 *
 * The badges are Apple's and Google's own artwork, served by their own badge services, unchanged (both companies require
 * that). Kept free of React and of anything the browser provides so `node --test` can run it (getTheApp.test.mjs).
 */

/** The App Store's number for the app (App Store Connect, App Information, "Apple ID"). */
export const APP_STORE_ID = '6801896864';
/** The app's id on Google Play (the same bundle id as on iOS). */
export const ANDROID_PACKAGE = 'com.gr8fulprayers.app';

export const APP_STORE_URL = `https://apps.apple.com/app/id${APP_STORE_ID}`;

/** The Play Store page. The referrer shows in the Play Console which part of the website the install came from. */
export function playStoreUrl(place) {
  const referrer = `utm_source=gr8fulprayers.com&utm_medium=${place}`;
  return `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}&referrer=${encodeURIComponent(referrer)}`;
}

export const BADGES = Object.freeze({
  apple: Object.freeze({
    store: 'apple',
    alt: 'Download on the App Store',
    src: 'https://toolbox.marketingtools.apple.com/api/badges/download-on-the-app-store/black/en-us?size=250x83',
    href: () => APP_STORE_URL,
  }),
  google: Object.freeze({
    store: 'google',
    alt: 'Get it on Google Play',
    src: 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png',
    href: (place) => playStoreUrl(place),
  }),
});

/** 'ios', 'android' or 'other' (a computer). iPadOS reports a Mac, so a "Mac" with a touch screen is an iPad. */
export function phoneKind({ userAgent, maxTouchPoints } = {}) {
  const ua = String(userAgent || '');
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && Number(maxTouchPoints) > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/** The badges to show: the one for the visitor's phone, both on a computer. */
export function badgesFor(kind) {
  if (kind === 'ios') return [BADGES.apple];
  if (kind === 'android') return [BADGES.google];
  return [BADGES.apple, BADGES.google];
}

/** Where the Android bar stays away: signing up and signing in, so nobody is pulled away halfway through. */
const NO_BAR_PATHS = ['/onboarding', '/auth'];

/** Whether the Android bar shows: an Android phone's browser, not the app, not the website added to the home screen,
 *  not while signing up or in, and not once closed. */
export function showsAndroidBar({ kind, native, standalone, closed, path }) {
  if (kind !== 'android' || native || standalone || closed) return false;
  const p = String(path || '/');
  return !NO_BAR_PATHS.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

/** Remembered on the phone once the bar is closed. */
export const BAR_CLOSED_KEY = 'gr8ful.getTheAppBarClosed';
